import { action, computed, makeObservable, observable } from 'mobx';
import { BindingProps, StoreNode } from '../../store';
import { Store } from '../../store/store';
import { InputState, input } from '../input';
import { JobModel, Team } from '../../entities';
import { isNonEmptyString } from '../../core';
import { PlayerFramesetState, PlayerSectionName } from '../playerFrameset';
import { notifyError } from '../../services/notifications';
import debounce from 'lodash/debounce';

type Props = BindingProps<{
  jobId: string,
  frameset: PlayerFramesetState,
  teamId?: string | null,
  refreshSearchMatches: () => void,
  clearSearch: () => void
}>

export type SearchMode = 'idle' | 'ask' | 'transcript';

export class PlayerSearchBarState
  extends StoreNode {
  constructor(store: Store, props: Props) {
    super(store, props);
    makeObservable(this);

    this.searchQuery = input(this, {
      name: 'searchQuery',
      onChange: () => {
        if (this.isTranscriptSearchMode) 
          this.refreshSearchMatchesDebounced();
      }
    });
  }

  readonly searchQuery: InputState;
  @observable showDropdown: boolean = false;
  @observable dropdownElement: HTMLElement | null = null;
  @observable searchMode: SearchMode = 'idle';
  @observable aiAnswerLoading: boolean = false;
  @observable aiSearchKey: string | null = null;
  @observable aiAnswer: string | null = null;

  @computed get teamId(): string | null {
    return this.resolvedProps.teamId ?? null;
  }

  @computed get jobId() {
    return this.resolvedProps.jobId;
  }
  
  @computed get frameset(): PlayerFramesetState | null {
    return this.resolvedProps.frameset ?? null;
  }

  @computed private get refreshSearchMatches(): () => void {
    return this.getResolvedProp('refreshSearchMatches', null);
  }

  @computed private get clearTranscriptSearch(): (invokePause?: boolean | null) => void {
    return this.getResolvedProp('clearSearch', null);
  }

  @computed get dropdownOpened(): boolean {
    return !!(this.showDropdown && this.searchQuery.value);
  }
  
  @computed get job(): JobModel | null {
    return this.store.maybeGetJob(this.jobId);
  }

  @computed get team(): Team | null {
    return this.store.teamManager.getTeam(this.teamId);
  }

  @computed get isWidgetMode() {
    return this.store.widgetService.isWidgetMode;
  }

  @computed get isAiProcessing() {
    const gptProvider = this.store.aiService.getAiProvider(this.job, this.teamId);

    if (gptProvider)
      return this.job?.isAiProcessing(gptProvider);

    return false;
  }

  @computed
  get cliprGptEnabled() {
    return this.store.aiService.getCliprGptEnabled(this.team);
  }

  @computed get isTranscriptSearchMode() {
    return this.cliprGptEnabled ? this.searchMode === 'transcript' : true;
  }

  @computed get isAskSearchMode() {
    return this.searchMode === 'ask';
  }

  @computed get isIdleSearchMode() {
    return this.cliprGptEnabled && this.searchMode === 'idle';
  }

  @computed get placeholder() {
    if (this.cliprGptEnabled && this.isAskSearchMode) 
      return 'Ask CLIPr GPT anything';

    return this.cliprGptEnabled ? 'Ask anything or search in transcript' : 'Search in transcript';
  }

  @computed
  get showIcon() {
    return !!this.searchQuery.value || !this.isIdleSearchMode;
  }

  private refreshSearchMatchesDebounced = debounce(() => this.refreshSearchMatches(), 500);

  @action
  setShowDropdown = (show: boolean) => {
    this.showDropdown = show;
  }

  @action
  setDropdownElement = (dropdownElement: HTMLElement | null) => {
    this.dropdownElement = dropdownElement;
  }

  @action
  handleAsk = async (evt: React.MouseEvent | React.KeyboardEvent, aiSearchKey?: string) => {
    evt.stopPropagation();
    this.setShowDropdown(false);
  
    this.configureUIForMobile();
  
    const query = aiSearchKey ?? this.searchQuery.value;
    if (this.isQueryInvalid(query)) 
      return;
  
    this.prepareForAiQuery(query);
    const response = await this.fetchAiAnswer(query!);
  
    if (response.error) {
      notifyError(this, 'Cannot extract answer. Try again.');
    } else {
      this.aiAnswer = response.answer?.result ?? null;
    }
  }
  
  private configureUIForMobile() {
    if (!this.store.ui.isMobile) 
      return;
  
    ['Index', 'Comments', 'Toolbar'].forEach(section => this.frameset?.hideSection(section as PlayerSectionName));
  }
  
  private isQueryInvalid(query?: string | null): boolean {
    return !query || !isNonEmptyString(query) || this.aiAnswerLoading;
  }
  
  private prepareForAiQuery(query: string | null) {
    this.searchMode = 'ask';
    this.aiAnswerLoading = true;
    this.aiSearchKey = query;
    this.searchQuery.value = null;
  }
  
  private async fetchAiAnswer(query: string) {
    const { user: profile } = this.store;
    const userId = !this.teamId ? profile?.id : undefined;
    const [res, err] = await this.store.api.getGPTAnswer({
      input: {
        question: query,
        jobId: this.jobId,
        teamId: this.teamId ?? undefined,
        userId: userId ?? undefined
      }
    });
  
    this.aiAnswerLoading = false;
    if (!res || err) 
      return { error: true };
    
    return { answer: res.getGPTAnswer };
  }

  @action
  handleSearch = (evt: React.MouseEvent) => {
    evt.stopPropagation();
    this.setShowDropdown(false);
    this.aiSearchKey = null;
    this.aiAnswer = null;
    this.searchMode = 'transcript';
    this.refreshSearchMatches();
  }

  @action
  handleKeyUp = (evt: React.KeyboardEvent) => {   
    if (!this.searchQuery.value) 
      return;

    if (this.searchMode === 'idle' && this.cliprGptEnabled) {
      this.setShowDropdown(true);
    }

    if (this.searchMode === 'ask' && evt.key === 'Enter') {
      this.handleAsk(evt, this.searchQuery.value);
      // Workaround to hide the keyboard on mobile
      (evt.target as HTMLElement).blur?.();
    }
  }

  @action
  handleBackToTranscriptButtonClick = () => {
    this.searchQuery.value = null;
    this.resetSearchMode();
    this.clearTranscriptSearch(false);
  }

  @action
  resetSearchMode = () => {
    this.aiSearchKey = null;
    this.aiAnswer = null;
    this.searchMode = 'idle';
  }

  @action
  reset = () => {
    this.searchQuery.value = null;
    this.resetSearchMode();
  }

  @action
  handleClear = () => {
    this.searchQuery.value = null;

    if (this.cliprGptEnabled) {
      return;
    }

    this.resetSearchMode();
    this.clearTranscriptSearch(false);
  }
}
